import { useEffect } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { useAuthStore } from '@clarix/auth-store';
import { publishAppModules, clearAppModules } from '@clarix/rbac';
import { AppSkeleton } from './AppSkeleton';
import { WorkspaceUnavailable } from './WorkspaceUnavailable';
import { buildApexUrl, buildWorkspaceUrl, useActiveWorkspaceSlug } from './getWorkspaceSlug';
import { IS_SINGLE_HOST } from '@/config/env';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { setCurrent } from '@/core/workspace';
import { useMe } from '@/core/api';
import { QuickMeetingProvider } from './quick-meeting';
import { useBootTrace } from '@/gateway/devtools/bootTrace';

/** Pull the HTTP status off an axios-shaped error without importing axios. */
function errorStatus(err: unknown): number | undefined {
  return (err as { response?: { status?: number } } | null)?.response?.status;
}

/**
 * The path the user actually asked for, query and hash included, so a
 * cross-origin hop to their workspace subdomain lands on it rather than on the
 * workspace home. `?tab=`/`#note-6` matter: settings pages and comment anchors
 * are both linked from emails.
 */
function currentPath(): string {
  const { pathname, search, hash } = window.location;
  return `${pathname}${search}${hash}`;
}

/**
 * Authenticated workspace gate. Validates membership + loads the /me cache,
 * then renders its child routes via <Outlet/>. The shared chrome (TopBar +
 * SidebarRail) is applied by the nested <AppShellLayout/> layout route, so
 * chrome-free pages (e.g. the AppLauncher at "/") can sit directly under this
 * gate without the rail/header.
 *
 * On mount:
 *   1. Reads workspace slug from URL subdomain (apex → no slug → onboarding).
 *   2. Fires /me?activeSlug=<urlSlug> to validate membership + load apps map.
 *   3. Bounces to apex /onboarding when /me says CREATE_WORKSPACE_OR_ACCEPT_INVITE.
 *   4. If user lands on a slug subdomain they aren't a member of, redirect to
 *      their default workspace (or onboarding if they don't own one).
 */
export function WorkspaceShell() {
  // In single-host mode the tenant is not in the URL — it's the JWT's workspace
  // slug, kept in the auth session. Everywhere else it's parsed from the host.
  const urlSlug = useActiveWorkspaceSlug();
  const dispatch = useAppDispatch();
  const current = useAppSelector((s) => s.workspace.current);

  const me = useMe({ activeSlug: urlSlug ?? undefined });
  useBootTrace('WorkspaceShell', { urlSlug, me: me.status, fetch: me.fetchStatus, hasData: !!me.data });

  // Single-host: keep the auth session's slug (→ the X-Workspace-Slug header)
  // aligned with the active workspace even when a token arrived without the
  // claim, so requests are tenant-scoped without any subdomain hop.
  useEffect(() => {
    if (!IS_SINGLE_HOST) return;
    const active = me.data?.active_workspace_slug;
    if (!active) return;
    if (useAuthStore.getState().workspaceSlug !== active) {
      useAuthStore.getState().setWorkspaceSlug(active);
    }
  }, [me.data?.active_workspace_slug]);

  // Per-app MODULE entitlement, into the shared @clarix/rbac store so REMOTES
  // can read it. They each run their own QueryClient and never call /me, so
  // without this the only per-app answer in the app lives in a cache they
  // cannot see — and the flat `disabledModules` on the permission store cannot
  // answer it, because module codes repeat across apps (see app-module-store).
  //
  // Republished on every /me, heartbeat refetches included, so a platform
  // admin's toggle reaches an open tab within the poll interval. Cleared on
  // unmount: a workspace switch must not leave the previous tenant's
  // entitlement standing.
  useEffect(() => {
    if (!me.data) return;
    publishAppModules(me.data.active_workspace_apps);
  }, [me.data]);

  useEffect(() => () => clearAppModules(), []);

  useEffect(() => {
    if (!me.data) return;
    const activeSlug = me.data.active_workspace_slug;
    if (!activeSlug) return;
    const match = me.data.workspaces.find((w) => w.slug === activeSlug);
    if (!match) return;
    if (current?.slug !== match.slug) {
      dispatch(setCurrent({ id: match.id, slug: match.slug, name: match.name }));
    }
  }, [me.data, current?.slug, dispatch]);

  if (me.isLoading) return <AppSkeleton />;

  // /me failed and we have no cached session to render from.
  //
  // A 401 is a genuine session failure — the api-client interceptor has already
  // cleared the token, so falling through to /login (which now shows the sign-in
  // form, not an interstitial) is correct.
  //
  // Every OTHER failure (CORS / network / 5xx / timeout) is a BACKEND outage, not
  // an auth problem. Bouncing to /login would strand a still-signed-in user on a
  // "You're already signed in → Continue to your workspace" card whose button
  // only lands them back here — a loop that hides the real cause. Hand it to
  // WorkspaceUnavailable instead: it verifies with the public /system/status
  // probe and either retries /me in place (transient blip) or shows the
  // self-polling maintenance page, which recovers on its own the moment the
  // backend is back.
  //
  // When `me.data` IS present the failure is a background heartbeat refetch
  // (see refetchInterval in me.api.ts) — keep the app on screen; the per-app
  // AppRoute outage handling owns what the user sees from there.
  if (me.isError && !me.data) {
    if (errorStatus(me.error) === 401) return <Navigate to="/login" replace />;
    return <WorkspaceUnavailable onRetry={() => void me.refetch()} />;
  }

  if (me.data?.next_step === 'CREATE_WORKSPACE_OR_ACCEPT_INVITE') {
    // Every session that reaches this shell is a TENANT session, so a user with
    // no workspace really does belong in onboarding. (The platform-admin console
    // is a separate app on its own origin now; it never issues a token this
    // shell can hold, so there is nothing to redirect to /admin.)
    //
    // Onboarding lives on apex. From a slug subdomain this requires a full
    // navigation; from apex an SPA <Navigate> is enough. Single-host is always
    // "apex" (one origin) → an in-SPA <Navigate> is all that's needed.
    if (urlSlug && !IS_SINGLE_HOST) {
      window.location.replace(buildApexUrl('/onboarding'));
      return <AppSkeleton />;
    }
    return <Navigate to="/onboarding" replace />;
  }

  // User landed on apex (no slug) but has a workspace → take them to it,
  // KEEPING the path they asked for.
  //
  // This used to hard-code '/'. Any apex deep link therefore lost its path on
  // the hop and dumped the user on the app launcher ("Choose an app") — which is
  // exactly what an emailed "Open my timesheet" button did, since the backend
  // built those links against the apex. The backend now emits the workspace
  // subdomain directly (packages/shared/src/app-url.ts), but preserving the path
  // here is still required: every link already sitting in someone's inbox points
  // at the apex, and this is the only thing that rescues them.
  //
  // Skipped in single-host mode: there is no workspace subdomain to hop to, and
  // getWorkspaceSlug() is always null there, so this would replace the apex with
  // itself forever. The workspace is resolved from the session instead.
  if (!IS_SINGLE_HOST && !urlSlug && me.data?.active_workspace_slug) {
    window.location.replace(buildWorkspaceUrl(me.data.active_workspace_slug, currentPath()));
    return <AppSkeleton />;
  }

  // User landed on a slug subdomain they aren't a member of → bounce to their
  // default workspace (or onboarding if they don't own one).
  //
  // The path is deliberately DROPPED here, unlike the apex hop above: this
  // crosses a tenant boundary, and a path from another workspace is meaningless
  // (or misleading) in this one — `/projects/42` names a different project, and
  // replaying it would 404 or, worse, look like a real record. Landing on the
  // workspace home is the honest outcome.
  if (urlSlug && me.data) {
    const knows = me.data.workspaces.some((w) => w.slug === urlSlug);
    if (!knows) {
      if (me.data.active_workspace_slug) {
        window.location.replace(buildWorkspaceUrl(me.data.active_workspace_slug, '/'));
      } else {
        window.location.replace(buildApexUrl('/onboarding'));
      }
      return <AppSkeleton />;
    }
  }

  // Above every remote, the launcher and chrome-hidden routes, so a recording survives them all.
  return (
    <QuickMeetingProvider>
      <Outlet />
    </QuickMeetingProvider>
  );
}
